import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  TextField,
  Chip,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';

/**
 * 멘션 기능이 통합된 입력 컴포넌트
 * @을 입력하면 참여자 목록 팝업이 표시됩니다.
 *
 * Props:
 *  - value: string - 현재 입력값
 *  - onChange: (value, mentions) => void - 값 변경 시 콜백 (입력값, 멘션된 사용자 배열)
 *  - participants: [{usrUuid, usrNm, emoticon}] - 채팅방 참여자 목록
 *  - disabled: boolean
 *  - placeholder: string
 *  - onKeyPress: (e) => void
 *  - inputRef: ref
 */
const MentionInput = ({
  value,
  onChange,
  participants = [],
  disabled = false,
  placeholder = '메시지를 입력하세요. @로 멘션',
  onKeyPress,
  inputRef: externalInputRef,
}) => {
  const [showPopup, setShowPopup] = useState(false);
  const [popupFilter, setPopupFilter] = useState('');
  const [mentionStart, setMentionStart] = useState(-1); // @ 위치
  const [mentions, setMentions] = useState([]); // [{usrUuid, usrNm}]
  const [selectedIndex, setSelectedIndex] = useState(0);

  const internalInputRef = useRef(null);
  const inputRef = externalInputRef || internalInputRef;

  // 참여자 필터링
  const filteredParticipants = participants.filter(p =>
    p.usrNm?.toLowerCase().includes(popupFilter.toLowerCase())
  );

  // 멘션 삽입
  const insertMention = useCallback((participant) => {
    const beforeAt = value.substring(0, mentionStart);
    const afterKeyword = value.substring(mentionStart + 1 + popupFilter.length);
    const mentionText = `@${participant.usrNm} `;
    const newValue = beforeAt + mentionText + afterKeyword;

    // 이미 멘션된 사용자가 아니면 추가
    const alreadyMentioned = mentions.find(m => m.usrUuid === participant.usrUuid);
    const newMentions = alreadyMentioned
      ? mentions
      : [...mentions, { usrUuid: participant.usrUuid, usrNm: participant.usrNm }];

    setMentions(newMentions);
    setShowPopup(false);
    setMentionStart(-1);
    setPopupFilter('');
    setSelectedIndex(0);

    onChange(newValue, newMentions);

    // 커서를 삽입된 멘션 뒤로 이동
    setTimeout(() => {
      if (inputRef.current) {
        const cursorPos = beforeAt.length + mentionText.length;
        inputRef.current.setSelectionRange(cursorPos, cursorPos);
        inputRef.current.focus();
      }
    }, 10);
  }, [value, mentionStart, popupFilter, mentions, onChange, inputRef]);

  // 입력 변경 처리
  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;

    // @ 탐지 - 커서 앞에서 @ 찾기
    const textBeforeCursor = newValue.substring(0, cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf('@');

    if (atIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(atIndex + 1);
      // 공백이 없고 @ 이후 텍스트면 팝업 표시
      if (!textAfterAt.includes(' ')) {
        setMentionStart(atIndex);
        setPopupFilter(textAfterAt);
        setShowPopup(true);
        setSelectedIndex(0);
      } else {
        setShowPopup(false);
        setMentionStart(-1);
      }
    } else {
      setShowPopup(false);
      setMentionStart(-1);
    }

    // 멘션이 삭제된 경우 mentions 배열에서도 제거
    const updatedMentions = mentions.filter(m =>
      newValue.includes(`@${m.usrNm}`)
    );
    if (updatedMentions.length !== mentions.length) {
      setMentions(updatedMentions);
    }

    onChange(newValue, updatedMentions);
  }, [mentions, onChange]);

  // 키보드 네비게이션
  const handleKeyDown = useCallback((e) => {
    // 한글 IME 조합 중(isComposing)이면 Enter 무시 → 마지막 글자 중복 방지
    if (e.key === 'Enter' && e.nativeEvent?.isComposing) {
      e.preventDefault();
      return;
    }

    if (!showPopup || filteredParticipants.length === 0) {
      if (onKeyPress) onKeyPress(e);
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, filteredParticipants.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      if (filteredParticipants[selectedIndex]) {
        e.preventDefault();
        insertMention(filteredParticipants[selectedIndex]);
        return;
      }
    } else if (e.key === 'Escape') {
      setShowPopup(false);
      setMentionStart(-1);
    } else {
      if (onKeyPress) onKeyPress(e);
    }
  }, [showPopup, filteredParticipants, selectedIndex, insertMention, onKeyPress]);

  // 팝업 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = () => {
      if (showPopup) setShowPopup(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showPopup]);

  // 메시지 텍스트에서 @멘션 부분을 파란색 칩으로 렌더링 (미리보기용)
  const renderValueWithMentions = () => {
    if (mentions.length === 0) return null;
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, px: 1, pt: 0.5 }}>
        {mentions.map(m => (
          <Chip
            key={m.usrUuid}
            label={`@${m.usrNm}`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ position: 'relative', flexGrow: 1 }}>
      {/* 멘션 칩 미리보기 */}
      {mentions.length > 0 && renderValueWithMentions()}

      {/* 입력창 */}
      <TextField
        inputRef={inputRef}
        fullWidth
        multiline
        maxRows={3}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        variant="outlined"
        size="small"
        disabled={disabled}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
          }
        }}
      />

      {/* 멘션 팝업 */}
      {showPopup && filteredParticipants.length > 0 && (
        <Paper
          elevation={8}
          sx={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            right: 0,
            mb: 0.5,
            maxHeight: 200,
            overflow: 'auto',
            zIndex: 1300,
            border: '1px solid #e0e0e0',
            borderRadius: 2,
          }}
          onClick={e => e.stopPropagation()}
        >
          <Box sx={{ p: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ px: 1, py: 0.5, display: 'block' }}>
              👤 멘션할 사용자 선택 (↑↓ 키로 이동, Enter 선택)
            </Typography>
          </Box>
          <List dense sx={{ py: 0 }}>
            {filteredParticipants.map((participant, index) => (
              <ListItem
                key={participant.usrUuid}
                button
                selected={index === selectedIndex}
                onClick={() => insertMention(participant)}
                sx={{
                  borderRadius: 1,
                  mx: 0.5,
                  mb: 0.5,
                  cursor: 'pointer',
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText',
                  },
                  '&:hover': {
                    backgroundColor: 'primary.light',
                  }
                }}
              >
                <ListItemAvatar sx={{ minWidth: 36 }}>
                  <Avatar
                    src={participant.emoticon}
                    sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'primary.main' }}
                  >
                    {participant.usrNm?.charAt(0) || <PersonIcon fontSize="small" />}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={participant.usrNm}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: index === selectedIndex ? 'bold' : 'normal' }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* 팝업이 열렸는데 결과 없을 때 */}
      {showPopup && filteredParticipants.length === 0 && popupFilter.length > 0 && (
        <Paper
          elevation={4}
          sx={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            right: 0,
            mb: 0.5,
            p: 1.5,
            zIndex: 1300,
            borderRadius: 2,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            '{popupFilter}'에 해당하는 참여자가 없습니다.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default MentionInput;
